import { Calendar, Package, Store } from "lucide-react"

import { OrderStatus, type Order } from "@/api/api-client"
import { Badge } from "@/components/ui/badge"
import { OrderStatusBadge } from "@/components/orders/OrderStatusBadge"
import { useLanguage } from "@/hooks/useLanguage"

import type { VendorOrderLabels } from "./vendor-order-types"

interface VendorOrderIdentityProps {
  masterOrder: Order
  subOrderId?: number
  displayStatus: OrderStatus
  formattedOrderDate: string
  totalWeight: number
  totalUnits?: number
  isPickup: boolean
  labels: Pick<VendorOrderLabels, "refId" | "client" | "statuses">
  weightLabel: string
}

export function VendorOrderIdentity({
  masterOrder,
  subOrderId,
  displayStatus,
  formattedOrderDate,
  totalWeight,
  totalUnits,
  isPickup,
  labels,
  weightLabel,
}: VendorOrderIdentityProps) {
  const { t } = useLanguage()
  const unitsWord = totalUnits === 1 ? t.units.unit : t.units.units
  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <OrderStatusBadge status={displayStatus} labels={labels.statuses} />
          {isPickup && (
            <Badge
              variant="outline"
              className="border-cyan-200 bg-cyan-50 text-xs font-bold uppercase tracking-wider text-cyan-700"
            >
              <Store className="mr-1 h-3 w-3" /> PICKUP
            </Badge>
          )}
        </div>
        <span className="flex shrink-0 items-center gap-1.5 text-sm font-bold text-foreground">
          <Calendar className="h-3.5 w-3.5" />
          {formattedOrderDate}
        </span>
      </div>
      <div>
        <h2 className="font-display text-4xl leading-none tracking-tighter text-foreground">
          {masterOrder.id}
        </h2>
        <p className="mt-1 text-xs font-bold uppercase tracking-widest text-muted-foreground">
          {labels.refId} {subOrderId}
        </p>
      </div>
      <div className="space-y-1">
        <span className="text-xs font-black uppercase tracking-widest text-muted-foreground">
          {labels.client}
        </span>
        <p className="text-sm font-bold uppercase tracking-wider text-foreground">
          {masterOrder.receiverName}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-4 pt-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <Package className="h-3.5 w-3.5" />{" "}
          {totalUnits != null && totalUnits > 0 ? `${totalUnits} ${unitsWord} · ` : ""}
          {weightLabel.replace("{weight}", totalWeight % 1 === 0 ? totalWeight.toString() : totalWeight.toFixed(2))}
        </span>
      </div>
    </div>
  )
}
